import React, { useEffect, useMemo, useState } from "react";
import { ref, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import StatCard from "./StatCard";
import RealTimeBalanceCard from "./RealTimeBalanceCard";
import toast from "react-hot-toast";

/**
 * AccountsOverview (refined)
 * - Removes "Add transaction" button (read-only overview here)
 * - Keeps History action per account
 * - Shows top summary: Assets (cash + bank), Farmers Owed (farmer_payable),
 *   Customers Owe Us (shop_receivable) and Liabilities
 * - Renders a small multi-line SVG chart for selected month showing daily totals
 *
 * Data source:
 * - Companies/{companyKey}/Accounts/{accountType}/{year}/{month}/{entryId}
 *
 * Notes:
 * - Chart is lightweight and uses per-day aggregation of transactions in the selected month.
 * - Direction: transactions with direction === "credit" increase the account,
 *   direction === "debit" decrease it (same math as balances).
 */

const ACCOUNT_TYPES = [
  { key: "farmer_payable", label: "Farmer Payable", color: "#fb7185" }, // rose
  { key: "farmer_advance", label: "Farmer Advance", color: "#f59e0b" }, // amber
  { key: "liabilities", label: "Liabilities", color: "#8b5cf6" }, // violet
  { key: "company_cash", label: "Company Cash", color: "#06b6d4" }, // cyan
  { key: "company_bank", label: "Company Bank", color: "#0ea5e9" }, // sky
  { key: "shop_receivable", label: "Shop Receivable", color: "#84cc16" }, // lime
];

const SUMMARY_KEYS = {
  assets: ["company_cash", "company_bank"],
  farmers_owed: ["farmer_payable"],
  customers_owe: ["shop_receivable"],
  liabilities: ["liabilities", "farmer_advance"], // show advances under liabilities summary if you prefer
};

const formatCurrency = (v) =>
  (Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " KES";

// Helper to get number of days in a month
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

const AccountsOverview = ({ companyKey }) => {
  const [transactionsMap, setTransactionsMap] = useState({}); // accountKey -> [entries]
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);

  // month/year selectors (defaults to current)
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));

  // History modal / view state
  const [historyOpenFor, setHistoryOpenFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!companyKey) return;
    const load = async () => {
      setLoading(true);
      try {
        const map = {};
        await Promise.all(
          ACCOUNT_TYPES.map(async (acct) => {
            const path = `Companies/${companyKey}/Accounts/${acct.key}/${year}/${month}`;
            try {
              const snap = await get(ref(database, path));
              map[acct.key] = Object.values(snap.val() || {});
            } catch (err) {
              console.warn("load account error", acct.key, err);
              map[acct.key] = [];
            }
          })
        );
        setTransactionsMap(map);
      } catch (err) {
        console.error("load accounts", err);
        toast.error("Could not load accounts");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyKey, year, month]);

  // balances per account (sum of transactions considering direction)
  const balances = useMemo(() => {
    const b = {};
    ACCOUNT_TYPES.forEach((acct) => {
      const arr = transactionsMap[acct.key] || [];
      b[acct.key] = arr.reduce((s, tx) => {
        const amt = Number(tx.amount || 0);
        return s + (tx.direction === "credit" ? amt : -amt);
      }, 0);
    });
    return b;
  }, [transactionsMap]);

  // summary numbers
  const summary = useMemo(() => {
    const totals = {};
    // assets
    totals.assets = SUMMARY_KEYS.assets.reduce((s, k) => s + (balances[k] || 0), 0);
    // farmers owed
    totals.farmers_owed = SUMMARY_KEYS.farmers_owed.reduce((s, k) => s + (balances[k] || 0), 0);
    // customers owe
    totals.customers_owe = SUMMARY_KEYS.customers_owe.reduce((s, k) => s + (balances[k] || 0), 0);
    // liabilities (include explicit liabilities; exclude assets)
    totals.liabilities = SUMMARY_KEYS.liabilities.reduce((s, k) => s + (balances[k] || 0), 0);
    // simple net position (assets - liabilities - farmers_owed + customers_owe)
    totals.net = totals.assets - totals.liabilities - totals.farmers_owed + totals.customers_owe;
    return totals;
  }, [balances]);

  // Build daily aggregates for selected accounts for charting
  // returns: { acctKey: [dailyTotal...index 0 => day 1] }
  const dailyAggregates = useMemo(() => {
    const days = daysInMonth(Number(year), Number(month));
    const agg = {};
    ACCOUNT_TYPES.forEach((acct) => {
      agg[acct.key] = new Array(days).fill(0);
      const entries = transactionsMap[acct.key] || [];
      entries.forEach((tx) => {
        // Use createdAt (ms) or date (ISO) fallback
        let d = tx.createdAt ? new Date(Number(tx.createdAt)) : tx.date ? new Date(tx.date) : null;
        if (!d || isNaN(d.getTime())) {
          // fallback to today
          d = new Date();
        }
        if (d.getFullYear() !== Number(year) || (d.getMonth() + 1) !== Number(month)) {
          return; // skip out-of-range
        }
        const day = d.getDate(); // 1..days
        const amt = Number(tx.amount || 0) * (tx.direction === "credit" ? 1 : -1);
        agg[acct.key][day - 1] += amt;
      });
    });
    return agg;
  }, [transactionsMap, year, month]);

  // Build paths for an SVG multi-line chart (simple mapping)
  const chartData = useMemo(() => {
    const days = daysInMonth(Number(year), Number(month));
    // determine global min/max across selected account lines to scale chart nicely
    let globalMax = 0;
    let globalMin = 0;
    ACCOUNT_TYPES.forEach((acct) => {
      const arr = dailyAggregates[acct.key] || [];
      arr.forEach((v) => {
        if (v > globalMax) globalMax = v;
        if (v < globalMin) globalMin = v;
      });
    });
    // add small padding
    const padding = Math.max(1, Math.abs(globalMax) * 0.1);
    globalMax = globalMax + padding;
    globalMin = globalMin - padding;

    // if all zeros, make range 0..1 to avoid division by zero
    if (globalMax === 0 && globalMin === 0) {
      globalMax = 1;
      globalMin = 0;
    }

    const height = 120;
    const width = Math.min(720, days * 18); // approximate
    const points = {}; // acctKey -> path string
    ACCOUNT_TYPES.forEach((acct) => {
      const arr = dailyAggregates[acct.key] || [];
      const coords = arr.map((val, idx) => {
        // x - evenly spaced by day
        const x = (idx / (days - 1 || 1)) * (width - 10) + 5;
        // y - invert because SVG y=0 is top
        const normalized = (val - globalMin) / (globalMax - globalMin || 1); // 0..1
        const y = height - normalized * (height - 10) - 5;
        return `${x},${y}`;
      });
      points[acct.key] = coords.join(" ");
    });

    return { width, height, points, globalMax, globalMin, days };
  }, [dailyAggregates, year, month]);

  // Load history rows for a specific account (on demand)
  const openHistory = async (acctKey) => {
    if (!companyKey || !acctKey) return;
    setHistoryOpenFor(acctKey);
    setHistoryLoading(true);
    try {
      const path = `Companies/${companyKey}/Accounts/${acctKey}/${year}/${month}`;
      const snap = await get(ref(database, path));
      const rows = Object.entries(snap.val() || {}).map(([id, r]) => ({ id, ...r }));
      // sort by createdAt asc
      rows.sort((a, b) => (Number(a.createdAt || 0) - Number(b.createdAt || 0)));
      setHistoryRows(rows);
    } catch (err) {
      console.error("loadHistory", err);
      toast.error("Failed to load history");
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <>
      {/* Real-Time Balance Card - Shows current balances across all time */}
      <RealTimeBalanceCard companyKey={companyKey} />

      <div className="bg-white rounded-lg border p-4 shadow-sm">
        {/* header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Monthly Accounts overview</h3>
            <p className="text-sm text-gray-500">Select month to view balances and trends</p>
          </div>

        <div className="flex items-center gap-2">
          <select className="border rounded px-2 py-1 text-sm" value={year} onChange={(e) => setYear(e.target.value)}>
            {/* show -3..+1 years for convenience */}
            {Array.from({ length: 5 }).map((_, i) => {
              const y = String(now.getFullYear() - 3 + i);
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>

          <select className="border rounded px-2 py-1 text-sm" value={month} onChange={(e) => setMonth(e.target.value)}>
            {Array.from({ length: 12 }).map((_, i) => {
              const m = String(i + 1);
              return <option key={m} value={m}>{m.padStart(2, "0")}</option>;
            })}
          </select>

          <button onClick={() => {
            // refresh current data
            setLoading(true);
            setTransactionsMap({});
            setTimeout(() => setLoading(false), 300); // effect will refetch because year/month remain same
          }} className="px-3 py-1 border rounded text-sm">Refresh</button>
        </div>
      </div>

      {/* summary row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <StatCard title="Assets" value={formatCurrency(summary.assets)} hint="Cash + Bank" accent="from-cyan-400" />
        <StatCard title="Farmers Owed" value={formatCurrency(summary.farmers_owed)} hint="What we owe farmers" accent="from-rose-400" />
        <StatCard title="Customers Owe" value={formatCurrency(summary.customers_owe)} hint="Receivables" accent="from-lime-400" />
        <StatCard title="Liabilities" value={formatCurrency(summary.liabilities)} hint="Company liabilities" accent="from-violet-400" />
      </div>

      {/* chart + legend */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-600">Daily trends — {year}-{String(month).padStart(2, "0")}</div>
          <div className="text-xs text-gray-500">Scale: {chartData.globalMin.toFixed(2)} to {chartData.globalMax.toFixed(2)}</div>
        </div>

        <div className="overflow-x-auto border rounded p-2">
          <svg width={chartData.width} height={chartData.height} className="block">
            {/* grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
              const y = chartData.height - g * (chartData.height - 10) - 5;
              return <line key={i} x1={0} x2={chartData.width} y1={y} y2={y} stroke="#eee" strokeWidth="0.5" />;
            })}

            {/* account lines */}
            {ACCOUNT_TYPES.map((acct) => {
              const pts = (chartData.points && chartData.points[acct.key]) || "";
              // stroke color fallback
              const stroke = acct.color || "#0ea5e9";
              if (!pts) return null;
              // convert space-separated "x,y" into path
              const coords = pts.split(" ");
              const d = coords.map((p, i) => {
                const [x, y] = p.split(",");
                return `${i === 0 ? "M" : "L"} ${Number(x).toFixed(2)} ${Number(y).toFixed(2)}`;
              }).join(" ");
              return (
                <g key={acct.key}>
                  <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeOpacity="0.9" />
                  {/* small circles */}
                  {coords.map((p, i) => {
                    const [x, y] = p.split(",");
                    return <circle key={i} cx={Number(x)} cy={Number(y)} r={1.4} fill={stroke} opacity={0.9} />;
                  })}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="mt-2 flex flex-wrap gap-3">
            {ACCOUNT_TYPES.map((acct) => (
              <div key={acct.key} className="flex items-center gap-2 text-xs">
                <span style={{ width: 12, height: 12, background: acct.color }} className="inline-block rounded-sm" />
                <span className="text-xs text-gray-700">{acct.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* account cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ACCOUNT_TYPES.map((acct) => (
          <div key={acct.key} className="border rounded p-3 bg-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-gray-500">{acct.label}</div>
                <div className="text-xl font-semibold text-slate-800 mt-1">{formatCurrency(balances[acct.key] || 0)}</div>
                <div className="text-xs text-gray-400 mt-1">{(transactionsMap[acct.key] || []).length} tx this month</div>
              </div>

              <div className="flex flex-col gap-2">
                <button onClick={() => openHistory(acct.key)} className="px-2 py-1 border rounded text-sm">History</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* History modal / pane */}
      {historyOpenFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">History — {ACCOUNT_TYPES.find(a=>a.key===historyOpenFor)?.label}</h4>
              <div className="flex gap-2">
                <button onClick={() => { setHistoryOpenFor(null); setHistoryRows([]); }} className="px-3 py-1 border rounded">Close</button>
                <button onClick={() => {
                  // Export CSV of loaded rows
                  if (!historyRows.length) { toast("No records to export"); return; }
                  const headers = ["id","date","amount","direction","type","reference","notes","createdBy"];
                  const escape = (v) => {
                    if (v == null) return "";
                    const s = String(v);
                    if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
                    return s;
                  };
                  const csv = [headers.join(",")]
                    .concat(historyRows.map(r => headers.map(h => escape(r[h] || r[h.toLowerCase()] || "")).join(",")))
                    .join("\r\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${companyKey}_${historyOpenFor}_${year}${month}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }} className="px-3 py-1 bg-cyan-600 text-white rounded">Export CSV</button>
              </div>
            </div>

            {historyLoading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : historyRows.length === 0 ? (
              <div className="text-sm text-gray-500">No records for this period</div>
            ) : (
              <div className="max-h-96 overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="p-2">Date</th>
                      <th className="p-2">Amount</th>
                      <th className="p-2">Direction</th>
                      <th className="p-2">Type</th>
                      <th className="p-2">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((r) => (
                      <tr key={r.id} className="border-b last:border-b-0">
                        <td className="p-2">{new Date(r.createdAt || r.date || Date.now()).toLocaleString()}</td>
                        <td className="p-2">{formatCurrency(r.amount)}</td>
                        <td className="p-2">{r.direction}</td>
                        <td className="p-2">{r.type}</td>
                        <td className="p-2">{r.createdBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default AccountsOverview;